import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "../services/api";
import { formatToDisplayDate, parseDisplayDate } from "../services/dateFormatter";
import Modal from "../components/Modal";
import Pagination from "../components/Pagination";
import DateInput from "../components/DateInput";

const EMPTY = {
  customer: "",
  work_type: "",
  description: "",
  start_date: "",
  completion_date: "",
  amount: "",
  tax_percentage: "18",
  work_order_number: "",
  invoice_date: "",
  status: "Unpaid",
};

const RECEIPT_EMPTY = {
  receipt_date: "",
  amount_received: "",
  tds_it: "",
  tds_gst: "",
};

export default function WorkOrders({ role }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState(() => searchParams.get("q") || "");
  const [customerFilter, setCustomerFilter] = useState(() => searchParams.get("customer") || "");
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get("status") || "");
  const [workTypeFilter, setWorkTypeFilter] = useState(() => searchParams.get("work_type") || "");
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(() => parseInt(searchParams.get("page") || "1", 10) || 1);

  const [workOrders, setWorkOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const pageSize = 20;
  const [totalWorkOrders, setTotalWorkOrders] = useState(0);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [viewData, setViewData] = useState(null);
  const [customerQuery, setCustomerQuery] = useState("");
  const [receipt, setReceipt] = useState(RECEIPT_EMPTY);
  const [receiptWO, setReceiptWO] = useState(null);
  const [stats, setStats] = useState(null);
  const [ordering, setOrdering] = useState(() => searchParams.get("ordering") || "");

  const toggleOrdering = field => {
    setOrdering(curr => {
      if (curr === field) return `-${field}`;
      if (curr === `-${field}`) return "";
      return field;
    });
  };

  const sortIndicator = field =>
    ordering === field ? "↑" : ordering === `-${field}` ? "↓" : "↕";

  const renderSortHeading = (label, field) => (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: ".35rem",
      }}
    >
      <span>{label}</span>
      <button
        type="button"
        onClick={() => toggleOrdering(field)}
        style={{
          border: "none",
          background: "transparent",
          color: "#94a3b8",
          cursor: "pointer",
          fontSize: ".9rem",
        }}
      >
        {sortIndicator(field)}
      </button>
    </div>
  );

  const buildQuery = (p = page) => {
    const parts = [`page=${p}`, `page_size=${pageSize}`];
    if (searchTerm) parts.push(`search=${encodeURIComponent(searchTerm)}`);
    if (customerFilter) parts.push(`customer=${encodeURIComponent(customerFilter)}`);
    if (statusFilter) parts.push(`status=${encodeURIComponent(statusFilter)}`);
    if (workTypeFilter) parts.push(`work_type=${encodeURIComponent(workTypeFilter)}`);
    if (ordering) parts.push(`ordering=${encodeURIComponent(ordering)}`);
    return parts.join("&");
  };

  const syncUrl = (nextPage = page) => {
    const params = {};
    if (searchTerm) params.q = searchTerm;
    if (customerFilter) params.customer = customerFilter;
    if (statusFilter) params.status = statusFilter;
    if (workTypeFilter) params.work_type = workTypeFilter;
    if (ordering) params.ordering = ordering;
    if (nextPage > 1) params.page = String(nextPage);
    setSearchParams(params, { replace: true });
  };

  const load = (p = page) => {
    api
      .get(`work-orders/?${buildQuery(p)}`)
      .then(r => {
        const data = r.data || {};
        setWorkOrders(data.results || []);
        setTotalWorkOrders(data.count || (data.results ? data.results.length : 0));
      })
      .catch(() => {});

    api
      .get(`customers/?page_size=2000`)
      .then(r => setCustomers(r.data.results || r.data))
      .catch(() => {});

    api
      .get(`work-orders/stats/`)
      .then(r => setStats(r.data))
      .catch(() => {});
  };

  useEffect(() => {
    load(page);
  }, [
    page,
    searchTerm,
    customerFilter,
    statusFilter,
    workTypeFilter,
    ordering,
  ]);

  useEffect(() => {
    syncUrl(page);
  }, [page, searchTerm, customerFilter, statusFilter, workTypeFilter, ordering, showDeleted]);

  const filteredCustomers = useMemo(() => {
    const query = customerQuery.trim().toLowerCase();
    if (!query) return customers;
    return customers.filter(c => c.name.toLowerCase().includes(query));
  }, [customerQuery, customers]);

  const close = () => {
    setModal(null);
    setForm(EMPTY);
    setEditId(null);
    setViewData(null);
    setReceipt(RECEIPT_EMPTY);
    setReceiptWO(null);
  };

  const openCreate = () => {
    setForm(EMPTY);
    setEditId(null);
    setModal("form");
  };

  const openEdit = wo => {
    setForm({
      customer: wo.customer,
      work_type: wo.work_type,
      description: wo.description,
      start_date: formatToDisplayDate(wo.start_date),
      completion_date: formatToDisplayDate(wo.completion_date),
      amount: wo.amount,
      tax_percentage: wo.tax_percentage,
      work_order_number: wo.work_order_number,
      invoice_date: formatToDisplayDate(wo.work_order_date),
      status: wo.status,
    });
    setEditId(wo.id);
    setModal("form");
  };

  const openView = wo => {
    setViewData(wo);
    setModal("view");
  };

  const openReceipt = wo => {
    setReceiptWO(wo);
    setReceipt(RECEIPT_EMPTY);
    setModal("receipt");
  };

  const save = async e => {
    e?.preventDefault?.();
    const payload = { ...form };
    const startDateIso = payload.start_date ? parseDisplayDate(payload.start_date) : '';
    const completionDateIso = payload.completion_date ? parseDisplayDate(payload.completion_date) : '';
    const invoiceDateIso = payload.invoice_date ? parseDisplayDate(payload.invoice_date) : '';

    if (payload.start_date && !startDateIso) {
      alert('Please enter Start Date in DD/MM/YYYY format.');
      return;
    }
    if (payload.completion_date && !completionDateIso) {
      alert('Please enter Completion Date in DD/MM/YYYY format.');
      return;
    }
    if (payload.invoice_date && !invoiceDateIso) {
      alert('Please enter Invoice Date in DD/MM/YYYY format.');
      return;
    }

    payload.start_date = startDateIso;
    if (completionDateIso) payload.completion_date = completionDateIso;
    if (invoiceDateIso) payload.work_order_date = invoiceDateIso;
    delete payload.invoice_date;

    try {
      if (editId) await api.put(`work-orders/${editId}/`, payload);
      else await api.post("work-orders/", payload);
      close();
      load();
    } catch (err) {
      const errorData = err.response?.data;
      let message = err.message || "Unknown error";
      if (errorData) {
        if (typeof errorData === "string") {
          message = errorData;
        } else if (errorData.error) {
          message = errorData.error;
        } else if (errorData.detail) {
          message = errorData.detail;
        } else if (typeof errorData === "object") {
          message = Object.entries(errorData)
            .map(([key, value]) =>
              `${key}: ${Array.isArray(value) ? value.join(" ") : value}`
            )
            .join(" | ");
        }
      }
      alert("Save failed: " + message);
    }
  };

  const saveReceipt = async e => {
    e.preventDefault();
    const payload = {};
    if (receipt.receipt_date) {
      const receiptDateIso = parseDisplayDate(receipt.receipt_date);
      if (!receiptDateIso) {
        alert('Please enter Receipt Date in DD/MM/YYYY format.');
        return;
      }
      payload.receipt_date = receiptDateIso;
    }
    if (receipt.amount_received)
      payload.amount_received = receipt.amount_received;
    if (receipt.tds_it) payload.tds_it = receipt.tds_it;
    if (receipt.tds_gst) payload.tds_gst = receipt.tds_gst;
    try {
      await api.patch(`work-orders/${receiptWO.id}/`, payload);
      close();
      load();
    } catch (err) {
      alert("Receipt save failed: " + err.message);
    }
  };

  const del = async id => {
    if (!window.confirm("Delete this work order?")) return;
    try {
      await api.delete(`work-orders/${id}/`);
      load();
    } catch {
      alert("Delete failed.");
    }
  };

  const restore = async id => {
    if (!window.confirm("Restore this work order?")) return;
    try {
      await api.post(`work-orders/${id}/restore/`);
      alert("Work order restored successfully");
      load(page);
    } catch (err) {
      alert("Restore failed: " + (err?.response?.data?.detail || err.message));
    }
  };

  const fmt = n => "₹" + parseFloat(n).toLocaleString("en-IN");
  const isAdmin = role === "Admin";
  const custName = id => customers.find(c => c.id === id)?.name || "—";

  const rAmt = parseFloat(receipt.amount_received) || 0;
  const rTdsIT = parseFloat(receipt.tds_it) || 0;
  const rTdsGST = parseFloat(receipt.tds_gst) || 0;
  const rNet = rAmt + rTdsIT + rTdsGST;
  const woGrand = receiptWO ? parseFloat(receiptWO.grand_total) : 0;
  const rawPending = woGrand - rNet;
  const rPending = rawPending >= 1.0 ? rawPending : 0;

  const statusBadge = s =>
    s === "Paid"
      ? "badge-emerald"
      : s === "Partial"
        ? "badge-purple"
        : "badge-red";

  return (
    <div>
      <div className="section-header">
        <div>
          <h2>Invoice for Work Orders</h2>
          <p>Manage invoices for individual work orders and ad-hoc services.</p>
        </div>
        {isAdmin && (
          <button className="btn btn-purple" onClick={openCreate}>
            <svg
              width="16"
              height="16"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 4v16m8-8H4"
              />
            </svg>
            Create Invoice for Work Order
          </button>
        )}
      </div>

      {/* Stats */}
      {stats && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
            gap: "1rem",
            marginBottom: "1.5rem",
          }}
        >
          <div
            style={{
              background: "rgba(16,185,129,.06)",
              border: "1px solid rgba(16,185,129,.2)",
              padding: "1rem",
              borderRadius: ".5rem",
            }}
          >
            <div style={{ color: "#94a3b8", fontSize: ".85rem" }}>
              Total Work Orders
            </div>
            <div style={{ color: "#34d399", fontSize: "1.5rem", fontWeight: 700 }}>
              {stats.total_work_orders}
            </div>
          </div>
          <div
            style={{
              background: "rgba(34,211,238,.06)",
              border: "1px solid rgba(34,211,238,.2)",
              padding: "1rem",
              borderRadius: ".5rem",
            }}
          >
            <div style={{ color: "#94a3b8", fontSize: ".85rem" }}>
              Total Amount
            </div>
            <div style={{ color: "#22d3ee", fontSize: "1.5rem", fontWeight: 700 }}>
              {fmt(stats.total_amount)}
            </div>
          </div>
          <div
            style={{
              background: "rgba(6,182,212,.06)",
              border: "1px solid rgba(6,182,212,.2)",
              padding: "1rem",
              borderRadius: ".5rem",
            }}
          >
            <div style={{ color: "#94a3b8", fontSize: ".85rem" }}>Received</div>
            <div style={{ color: "#06b6d4", fontSize: "1.5rem", fontWeight: 700 }}>
              {fmt(stats.total_received)}
            </div>
          </div>
          <div
            style={{
              background: "rgba(248,113,113,.06)",
              border: "1px solid rgba(248,113,113,.2)",
              padding: "1rem",
              borderRadius: ".5rem",
            }}
          >
            <div style={{ color: "#94a3b8", fontSize: ".85rem" }}>Pending</div>
            <div style={{ color: "#f87171", fontSize: "1.5rem", fontWeight: 700 }}>
              {fmt(stats.total_pending)}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div
        style={{
          display: "flex",
          gap: ".5rem",
          marginBottom: ".75rem",
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <input
          placeholder="Search work order or client..."
          value={searchTerm}
          onChange={e => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          style={{ padding: ".65rem", minWidth: 220, flex: 1, maxWidth: 320 }}
        />
        <select
          value={statusFilter}
          onChange={e => {
            setStatusFilter(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All Status</option>
          <option value="Unpaid">Unpaid</option>
          <option value="Partial">Partial</option>
          <option value="Paid">Paid</option>
        </select>
        <button className="btn btn-slate btn-sm" onClick={() => load(page)}>
          Refresh
        </button>
        <button
          className="btn btn-slate btn-sm"
          onClick={() => {
            setSearchTerm("");
            setCustomerFilter("");
            setStatusFilter("");
            setWorkTypeFilter("");
            setOrdering("");
            setPage(1);
          }}
        >
          Clear Filters
        </button>
        {isAdmin && <label style={{ display: 'flex', alignItems: 'center', gap: '.5rem', cursor: 'pointer', padding: '.5rem .75rem', background: '#334155', borderRadius: '.25rem', fontSize: '.875rem' }}>
          <input type="checkbox" checked={showDeleted} onChange={e => { setShowDeleted(e.target.checked); setPage(1); }} style={{ cursor: 'pointer' }} />
          Show Deleted
        </label>}
      </div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th>{renderSortHeading("Inv. No.", "work_order_number")}</th>
              <th>{renderSortHeading("Date", "work_order_date")}</th>
              <th>{renderSortHeading("Client", "customer__name")}</th>
              <th>Type</th>
              <th>Description</th>
              <th>{renderSortHeading("Total", "grand_total")}</th>
              <th>Pending</th>
              <th>{renderSortHeading("Status", "status")}</th>
              <th className="th-right">Actions</th>
            </tr>
            <tr>
              <th>
                <input
                  type="text"
                  placeholder="WO #"
                  value={searchTerm}
                  onChange={e => {
                    setSearchTerm(e.target.value);
                    setPage(1);
                  }}
                />
              </th>
              <th></th>
              <th>
                <input
                  type="text"
                  placeholder="Customer"
                  value={customerFilter}
                  onChange={e => {
                    setCustomerFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </th>
              <th>
                <input
                  type="text"
                  placeholder="Type"
                  value={workTypeFilter}
                  onChange={e => {
                    setWorkTypeFilter(e.target.value);
                    setPage(1);
                  }}
                />
              </th>
              <th></th>
              <th></th>
              <th></th>
              <th>
                <select
                  value={statusFilter}
                  onChange={e => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                >
                  <option value="">Any</option>
                  <option value="Unpaid">Unpaid</option>
                  <option value="Partial">Partial</option>
                  <option value="Paid">Paid</option>
                </select>
              </th>
              <th className="th-right"></th>
            </tr>
          </thead>
          <tbody>
            {workOrders.length === 0 && (
              <tr>
                <td colSpan="9">
                  <div className="empty-state">No work orders registered yet.</div>
                </td>
              </tr>
            )}
            {workOrders.map(wo => (
              <tr key={wo.id}>
                <td style={{ fontWeight: 700, color: "#c084fc" }}>
                  {wo.work_order_number || `WO-${wo.id}`}
                </td>
                <td className="date-cell" style={{ color: "#94a3b8", fontSize: ".8rem" }}>
                  {formatToDisplayDate(wo.work_order_date) || "—"}
                </td>
                <td style={{ color: "#f1f5f9" }}>{wo.customer_name || "—"}</td>
                <td style={{ color: "#cbd5e1" }}>{wo.work_type || "—"}</td>
                <td
                  style={{
                    color: "#cbd5e1",
                    maxWidth: 300,
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                    whiteSpace: "normal",
                  }}
                >
                  {wo.description || "—"}
                </td>
                <td className="amount-cell" style={{ color: "#34d399", fontWeight: 600 }}>
                  {fmt(wo.grand_total)}
                </td>
                <td
                  className="amount-cell"
                  style={{
                    color: parseFloat(wo.pending_amount) > 0 ? "#f87171" : "#34d399",
                    fontWeight: 600,
                  }}
                >
                  {fmt(wo.pending_amount)}
                </td>
                <td>
                  <span className={`badge ${statusBadge(wo.status)}`}>
                    {wo.status}
                  </span>
                </td>
                <td className="td-right">
                  <div className="actions-cell">
                    <button
                      className="btn btn-slate btn-sm"
                      onClick={() => openView(wo)}
                    >
                      View
                    </button>
                    {isAdmin && (
                      <button
                        className="btn btn-purple btn-sm"
                        onClick={() => openEdit(wo)}
                      >
                        Edit
                      </button>
                    )}
                    {wo.status !== "Paid" && (
                      <button
                        className="btn btn-cyan btn-sm"
                        onClick={() => openReceipt(wo)}
                      >
                        Receipt
                      </button>
                    )}
                    {isAdmin && (
                      <button
                        className="btn btn-red btn-sm"
                        onClick={() => del(wo.id)}
                      >
                        Del
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        page={page}
        setPage={setPage}
        total={totalWorkOrders}
        pageSize={pageSize}
      />

      {/* Create/Edit Form Modal */}
      {modal === "form" && (
        <Modal
          title={editId ? "EDIT INVOICE FOR WORK ORDER" : "CREATE INVOICE FOR WORK ORDER"}
          titleColor="purple"
          onClose={close}
        >
          <form className="form-grid" onSubmit={save}>
            <div className="field full-width">
              <label>Client Entity</label>
              <input
                type="text"
                list="wo-customer-list"
                placeholder="Search or select customer..."
                value={customerQuery}
                onChange={e => {
                  const query = e.target.value;
                  setCustomerQuery(query);
                  const selected = customers.find(c => c.name === query);
                  if (selected && form.customer !== String(selected.id)) {
                    setForm({ ...form, customer: selected.id });
                  }
                }}
                style={{ width: "100%" }}
              />
              <datalist id="wo-customer-list">
                {filteredCustomers.map(c => (
                  <option key={c.id} value={c.name} />
                ))}
              </datalist>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Work Type</label>
                <input
                  type="text"
                  placeholder="e.g., Installation, Maintenance"
                  value={form.work_type}
                  onChange={e => setForm({ ...form, work_type: e.target.value })}
                />
              </div>
              <div className="field">
                <label>Amount (₹)</label>
                <input
                  type="number"
                  required
                  value={form.amount}
                  onChange={e => setForm({ ...form, amount: e.target.value })}
                />
              </div>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Start Date</label>
                <DateInput
                  value={form.start_date}
                  onChange={v => setForm({ ...form, start_date: v })}
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div className="field">
                <label>Completion Date</label>
                <DateInput
                  value={form.completion_date}
                  onChange={v => setForm({ ...form, completion_date: v })}
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div className="field full-width">
              <label>Description</label>
              <textarea
                placeholder="Describe the work to be performed"
                value={form.description}
                onChange={e =>
                  setForm({ ...form, description: e.target.value })
                }
                rows="3"
              />
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Tax %</label>
                <input
                  type="number"
                  value={form.tax_percentage}
                  onChange={e =>
                    setForm({ ...form, tax_percentage: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>Status</label>
                <select
                  value={form.status}
                  onChange={e => setForm({ ...form, status: e.target.value })}
                >
                  <option value="Unpaid">Unpaid</option>
                  <option value="Partial">Partial</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>Invoice Number</label>
                <input
                  type="text"
                  value={form.work_order_number}
                  onChange={e =>
                    setForm({ ...form, work_order_number: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>Invoice Date</label>
                <DateInput
                  value={form.invoice_date}
                  onChange={v => setForm({ ...form, invoice_date: v })}
                  placeholder="DD/MM/YYYY"
                />
              </div>
            </div>

            <div className="field full-width">
              <button type="submit" className="btn btn-purple" style={{ width: "100%" }}>
                {editId ? "Update" : "Create"} Invoice for Work Order
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* View Modal */}
      {modal === "view" && viewData && (
        <Modal
          title="INVOICE FOR WORK ORDER DETAILS"
          titleColor="amber"
          wide
          onClose={close}
          footer={
            <div style={{ display: "flex", gap: ".5rem" }}>
              {viewData.is_deleted && isAdmin && <button className="btn btn-green btn-sm" onClick={() => { restore(viewData.id); close(); }}>Restore</button>}
              <button className="btn btn-slate btn-sm" onClick={close}>
                Dismiss
              </button>
            </div>
          }
        >
          {viewData.is_deleted && <div style={{ padding: ".5rem", background: "#7c2d12", borderRadius: ".25rem", color: "#fed7aa", fontSize: ".875rem", marginBottom: ".75rem" }}>
            ⚠️ This record was deleted on {new Date(viewData.deleted_at).toLocaleString()}
          </div>}
          <div className="detail-grid">
            {[
              ["Invoice No", viewData.work_order_number || `WO-${viewData.id}`],
              ["Invoice Date", formatToDisplayDate(viewData.work_order_date) || "—"],
              ["Customer", viewData.customer_name || "—"],
              ["Type", viewData.work_type || "—"],
              ["Amount", fmt(viewData.amount)],
              ["Tax", fmt(viewData.tax_value)],
              ["Total", fmt(viewData.grand_total)],
              ["Received", fmt(viewData.net_received_total || 0)],
              ["Pending", fmt(viewData.pending_amount)],
              ["Status", viewData.status],
            ].map(([k, v]) => (
              <div key={k} className="detail-row">
                <span className="dk purple">{k}:</span>
                <span style={{ color: "#cbd5e1" }}>{v}</span>
              </div>
            ))}
            {viewData.description && (
              <>
                <div className="detail-divider"></div>
                <div className="detail-row description">
                  <span className="dk purple">Description:</span>
                  <span style={{ color: "#cbd5e1", whiteSpace: "pre-wrap", wordWrap: "break-word", overflowWrap: "break-word" }}>
                    {viewData.description}
                  </span>
                </div>
              </>
            )}
          </div>
        </Modal>
      )}

      {/* Receipt Modal */}
      {modal === "receipt" && receiptWO && (
        <Modal title="RECORD PAYMENT RECEIPT" titleColor="cyan" wide onClose={close}>
          <form onSubmit={saveReceipt} className="form-grid">
            <div className="receipt-summary-grid">
              <div className="receipt-card receipt-card-blue">
                <div className="receipt-card-label">Invoice No</div>
                <div className="receipt-card-value">{receiptWO.work_order_number || `WO-${receiptWO.id}`}</div>
              </div>
              <div className="receipt-card receipt-card-cyan">
                <div className="receipt-card-label">Invoice Date</div>
                <div className="receipt-card-value">{formatToDisplayDate(receiptWO.work_order_date) || "—"}</div>
              </div>
              <div className="receipt-card receipt-card-green">
                <div className="receipt-card-label">Client</div>
                <div className="receipt-card-value">{receiptWO.customer_name || "—"}</div>
              </div>
              <div className="receipt-card receipt-card-red">
                <div className="receipt-card-label">Status</div>
                <div className="receipt-card-value" style={{ color: receiptWO.status === "Paid" ? "#16a34a" : receiptWO.status === "Partial" ? "#7c3aed" : "#ef4444" }}>
                  {receiptWO.status}
                </div>
              </div>
            </div>

            <div className="receipt-metrics-grid">
              <div className="receipt-card receipt-card-outline">
                <div className="receipt-card-label">Invoice Total</div>
                <div className="receipt-card-value">{fmt(receiptWO.grand_total)}</div>
              </div>
              <div className="receipt-card receipt-card-outline">
                <div className="receipt-card-label">Paid So Far</div>
                <div className="receipt-card-value">{fmt(receiptWO.net_received_total || 0)}</div>
              </div>
              <div className="receipt-card receipt-card-outline">
                <div className="receipt-card-label">Outstanding</div>
                <div className="receipt-card-value" style={{ color: "#ef4444" }}>{fmt(receiptWO.pending_amount)}</div>
              </div>
            </div>

            <div className="form-divider"></div>

            <div className="grid-2">
              <div className="field">
                <label>Receipt Date</label>
                <DateInput
                  value={receipt.receipt_date}
                  onChange={v => setReceipt({ ...receipt, receipt_date: v })}
                  placeholder="DD/MM/YYYY"
                />
              </div>
              <div className="field">
                <label>Amount Received</label>
                <input
                  type="number"
                  value={receipt.amount_received}
                  onChange={e =>
                    setReceipt({ ...receipt, amount_received: e.target.value })
                  }
                />
              </div>
            </div>

            <div className="grid-2">
              <div className="field">
                <label>TDS IT</label>
                <input
                  type="number"
                  value={receipt.tds_it}
                  onChange={e =>
                    setReceipt({ ...receipt, tds_it: e.target.value })
                  }
                />
              </div>
              <div className="field">
                <label>TDS GST</label>
                <input
                  type="number"
                  value={receipt.tds_gst}
                  onChange={e =>
                    setReceipt({ ...receipt, tds_gst: e.target.value })
                  }
                />
              </div>
            </div>

            <div style={{ background: "rgba(34,211,238,.06)", padding: "1rem", borderRadius: ".5rem", marginBottom: "1rem" }}>
              <div style={{ fontSize: ".85rem", color: "#94a3b8" }}>
                Remaining Balance
              </div>
              <div
                style={{
                  fontSize: "1.5rem",
                  fontWeight: 700,
                  color: rPending > 0 ? "#f87171" : "#34d399",
                }}
              >
                {fmt(rPending)}
              </div>
            </div>

            <div className="field full-width">
              <button
                type="submit"
                className="btn btn-cyan"
                style={{ width: "100%" }}
              >
                Record Receipt
              </button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
